// Layer 4 (UI) — the library's context panel (locked comp
// `.impeccable/mocks/decision/documents.html` B): a titled fact list shaped
// per document kind, each ending with who is signed on the published version.
// A training module in review carries the Advisory approval gate: the Agent's
// draft routed to its Human reviewer, ink-filled Approve and a ghost Request
// changes (both inert at this grade), and who re-signs once it publishes.
// Renders `Scene.transform.ts` `revisionContext()`; derives nothing.

import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { GATE_GHOST_BUTTON, GATE_PRIMARY_BUTTON } from '../advisory/gate-ui';
import { displayOverrideAuthority, type RevisionContext } from './Scene.transform';
import type { DocumentSignoffs, PublishEffect } from './TrainingCompliance.transform';
import { FACT_BLOCK, FACT_CAP, FACT_ROW, REF } from './documents-ui';

type RevisionPanelProps = {
  context: RevisionContext;
};

const TITLE: Record<NonNullable<RevisionContext>['kind'], string> = {
  procedure: 'Sources and controls',
  'round-sheet': 'This shift',
  training: 'Audience and history',
};

export function RevisionPanel({ context }: RevisionPanelProps) {
  if (!context) return null;
  const title = context.kind === 'training' && context.pendingRevision ? 'Revision and routing' : TITLE[context.kind];
  return (
    <div className="px-4 py-3.5" data-context-panel={context.kind}>
      <h2 className="mb-2.5 text-[13px] font-semibold text-ink-primary">{title}</h2>
      {context.kind === 'procedure' ? (
        <ProcedureContextBody context={context} />
      ) : context.kind === 'round-sheet' ? (
        <RoundSheetContextBody context={context} />
      ) : (
        <TrainingContextBody context={context} />
      )}
    </div>
  );
}

/** "Signed on <version>": the current count against the required, then who is stale or unsigned. */
function SignedOn({ signoffs }: { signoffs?: DocumentSignoffs }) {
  if (!signoffs) return null;
  return (
    <div data-signed-on={signoffs.documentId}>
      <p className={cn(FACT_CAP, 'pt-3')}>Signed on {signoffs.version}</p>
      <div className={FACT_ROW}>
        <span className="text-ink-secondary">Current</span>
        <span className="whitespace-nowrap text-right font-semibold tabular-nums text-ink-primary">
          {signoffs.current} <span className="font-normal text-muted">of {signoffs.required}</span>
        </span>
      </div>
      {signoffs.stale.length > 0 ? (
        <div className={FACT_ROW}>
          <span className="text-ink-secondary">Stale</span>
          <span className="text-right font-semibold text-[var(--state-blocked)]">
            {signoffs.stale.map((person) => `${person.name} · ${person.signedVersion}`).join(', ')}
          </span>
        </div>
      ) : null}
      {signoffs.unsigned.map((group) => (
        <div key={group.dueAt} className={FACT_ROW}>
          <span className="text-ink-secondary">Unsigned, due {group.dueLabel}</span>
          <span className={cn('text-right font-semibold', group.pastDue ? 'text-[var(--state-blocked)]' : 'text-ink-primary')}>
            {group.names.join(', ')}
          </span>
        </div>
      ))}
    </div>
  );
}

function ProcedureContextBody({ context }: { context: Extract<RevisionContext, { kind: 'procedure' }> }) {
  return (
    <div>
      <p className={cn(FACT_CAP, 'pt-0')}>Source sections</p>
      <div className="flex flex-wrap gap-1 border-b border-[var(--color-divider)] py-1.5">
        {context.sourceSections.map((section) => (
          <span key={section} className={REF}>{section}</span>
        ))}
      </div>

      <p className={cn(FACT_CAP, 'pt-3')}>Control loops</p>
      {context.controlLoops.map((loop) => (
        <div key={loop.tag} className={cn(FACT_ROW, 'grid-cols-[auto_minmax(0,1fr)] gap-3.5')}>
          <span className="font-mono text-[11.5px] text-ink-secondary">{loop.tag}</span>
          <span className="text-right font-medium text-ink-primary">{loop.expectedMode}</span>
        </div>
      ))}

      {context.interlocks.length > 0 ? (
        <>
          <p className={cn(FACT_CAP, 'pt-3')}>Linked interlocks</p>
          {context.interlocks.map((il) => (
            <div key={il.id} className={FACT_ROW}>
              <span className="text-ink-secondary">
                <span className="font-mono text-[11px] text-ink-primary">{il.id}</span> {il.label}
              </span>
              <span className="whitespace-nowrap text-right text-[11px] text-muted">{displayOverrideAuthority(il.overrideAuthority)}</span>
            </div>
          ))}
        </>
      ) : null}

      <SignedOn signoffs={context.signoffs} />

      {context.changeEvents.length > 0 ? (
        <>
          <p className={cn(FACT_CAP, 'pt-3')}>Change history</p>
          {context.changeEvents.map((event) => (
            <div key={event.id} className={FACT_BLOCK}>
              <span className="font-mono text-[11.5px] font-semibold text-ink-primary">{event.revision}</span>
              <span className="text-muted"> · {event.at}</span>
              <span className="block">{event.summary}</span>
              {event.flagsTrainingId ? (
                <span className="mt-0.5 flex flex-wrap items-center gap-1">
                  <span className="text-muted">Flagged</span>
                  <span className={REF} title={event.flagsTrainingTitle}>{event.flagsTrainingId}</span>
                  <span className="text-muted">for revision</span>
                </span>
              ) : null}
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

function RoundSheetContextBody({ context }: { context: Extract<RevisionContext, { kind: 'round-sheet' }> }) {
  return (
    <div>
      <div className={cn(FACT_ROW, 'grid-cols-[auto_minmax(0,1fr)]')}>
        <span className="whitespace-nowrap text-ink-secondary">Circuit</span>
        <span className="text-right font-semibold text-ink-primary">
          {context.circuit} <span className="text-[11px] font-normal text-muted">· {context.shiftLabel}</span>
        </span>
      </div>
      {context.lastReading ? (
        <div className={cn(FACT_ROW, 'grid-cols-[auto_minmax(0,1fr)]')}>
          <span className="whitespace-nowrap text-ink-secondary">Last reading</span>
          <span className="text-right font-semibold text-ink-primary">
            {context.lastReading.reading}{' '}
            <span className="text-[11px] font-normal text-muted">
              · {context.lastReading.check} · {context.lastReading.dueAt}
            </span>
          </span>
        </div>
      ) : null}

      {context.progress.flagged.length > 0 ? (
        <>
          <p className={cn(FACT_CAP, 'pt-3')}>Flagged this shift</p>
          {context.progress.flagged.map((task) => (
            <div key={task.id} className={FACT_BLOCK} data-flagged-task={task.id}>
              <span className="block font-semibold text-ink-primary">{task.check}</span>
              {task.reading ? <span className="block font-semibold text-[var(--state-warning)]">{task.reading}</span> : null}
              <span className="block text-muted">{task.badLooksLike}</span>
            </div>
          ))}
        </>
      ) : null}

      {context.nextDue ? (
        <>
          <p className={cn(FACT_CAP, 'pt-3')}>Next due</p>
          <div className={FACT_ROW}>
            <span className="text-ink-secondary">{context.nextDue.check}</span>
            <span className="text-right font-semibold tabular-nums text-ink-primary">{context.nextDue.dueAt}</span>
          </div>
        </>
      ) : null}

      <SignedOn signoffs={context.signoffs} />
    </div>
  );
}

/** The one decision object on the route (DESIGN.md § Card or hairline): the review gate. */
function ReviewGate({
  reviewer,
  seat,
  status,
  nextVersion,
  effect,
}: {
  reviewer: string;
  seat: string;
  status: string;
  nextVersion?: string;
  effect?: PublishEffect;
}) {
  return (
    <div
      data-approval-gate="draft"
      className="mt-2.5 rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-3.5 py-3"
    >
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-primary">
        <ActorChip actor="Agent" />
        <span className="text-xs text-muted">drafted {nextVersion} →</span>
        <ActorChip actor="Human">{`${reviewer} · ${seat}`}</ActorChip>
      </div>
      <p className="mt-1.5 text-xs text-muted">{status}</p>
      <div className="mt-2.5 flex gap-2">
        <button type="button" disabled className={cn(GATE_PRIMARY_BUTTON, 'h-8 disabled:opacity-100')}>
          Approve
        </button>
        <button type="button" disabled className={cn(GATE_GHOST_BUTTON, 'h-8 disabled:opacity-100')}>
          Request changes
        </button>
      </div>
      {effect && effect.resign.length > 0 ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-secondary" data-gate-effect>
          On publish, re-sign
          {effect.resign.map((person) => (
            <ActorChip key={person.personId} actor="Human">{person.name}</ActorChip>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TrainingContextBody({ context }: { context: Extract<RevisionContext, { kind: 'training' }> }) {
  const revision = context.pendingRevision;
  return (
    <div>
      <div className={FACT_ROW}>
        <span className="text-ink-secondary">Audience</span>
        <span className="text-right font-semibold text-ink-primary">{context.audienceRole}</span>
      </div>

      <p className={cn(FACT_CAP, 'pt-3')}>Source</p>
      {context.sourceRefs.map((ref) => (
        <div key={ref} className={FACT_BLOCK}>{ref}</div>
      ))}

      {revision ? (
        <>
          <p className={cn(FACT_CAP, 'pt-3')}>Change event</p>
          <div className={FACT_ROW}>
            <span className="flex items-center gap-1.5 text-ink-secondary">
              Case <span className={REF}>{revision.sourceCaseLabel}</span>
            </span>
            <span className="text-right text-[11px] text-muted">diagnosed 05 Jun</span>
          </div>
          <div className={FACT_BLOCK}>
            FMEA row · {revision.fmeaRowLabel} <span className="text-muted">· pending approval</span>
          </div>
          <ReviewGate
            reviewer={revision.reviewerName}
            seat={revision.reviewSeat}
            status={revision.status}
            nextVersion={context.publishEffect?.nextVersion}
            effect={context.publishEffect}
          />
        </>
      ) : null}

      <SignedOn signoffs={context.signoffs} />

      <p className={cn(FACT_CAP, 'pt-3')}>Version history</p>
      {context.versionHistory.map((entry) => (
        <div key={entry.version} className={FACT_BLOCK}>
          <span className="font-mono text-[11.5px] font-semibold text-ink-primary">{entry.version}</span>
          <span className="text-muted"> · {entry.publishedAt}</span>
          <span className="block">{entry.source}</span>
        </div>
      ))}

      {context.refresherTargets.length > 0 ? (
        <>
          <p className={cn(FACT_CAP, 'pt-3')}>Refresher trigger</p>
          {context.refresherTargets.map((target) => (
            <div key={target.personName} className={FACT_ROW}>
              <span className="text-ink-secondary">
                {target.personName} · {target.role}
              </span>
              <span className="font-mono text-[11.5px] font-semibold text-ink-primary">{target.trainedOnVersion}</span>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}
